import Notify from 'notifyjs';
import { constants as notificationConstants } from '../../../modules/notifications';
import { playSound as utilsPlaySound } from '../../../store/features/realtime/presence/utils';
import * as devices from '../../../modules/devices';

window.Notify = Notify;

function updateMessageLevel(level) {
  return level === 'info' ? 'warning' : level;
}

function updateMessageHideDelay(hideDelay, level) {
  return hideDelay == null
    ? notificationConstants.HIDE_DELAYS_MAP[level]
    : hideDelay;
}
function updateMessageParams(params) {
  const level = updateMessageLevel(params?.level);
  return {
    ...params,
    level,
    hideDelay: updateMessageHideDelay(params?.hideDelay, level),
  };
}

export default angular
  .module('doxy.common.components.notification', [])
  .factory(
    'notificationFactory',
    function (
      $window,
      $translate,
      $q,
      User,
      notifications,
      Browser,
      $rootScope,
      $ngRedux
    ) {
      const closeAllOrig = notifications.closeAll;
      notifications.closeAll = function () {
        closeAllOrig();
        $rootScope.$emit('notifications:closeAll');
      };

      // Wrapper function that gets current speaker device ID from Redux state
      async function playSound(sound) {
        const state = $ngRedux.getState();
        const currentSpeakerId = devices.selectors.getCurrentSpeakerId(state);
        return utilsPlaySound(sound, currentSpeakerId);
      }

      function getPermissionStatus() {
        return $window.Notification?.permission;
      }
      function isPermissionResolved() {
        return (
          getPermissionStatus() === 'granted' ||
          getPermissionStatus() === 'denied'
        );
      }

      function isSupported() {
        return Boolean(isPermissionResolved() || Notify.isSupported());
      }

      async function getTranslations(message, data) {
        const baseTranslationPath = 'notification';
        const translationKeys = ['title', 'body'].map((key) =>
          [baseTranslationPath, message, key].join('.')
        );

        const translations = await $translate(translationKeys, data);

        const translationsResult = {};
        for (const translationKey of translationKeys) {
          const extractedKey = translationKey.split('.').pop();
          translationsResult[extractedKey] = translations[translationKey];
        }
        return translationsResult;
      }

      function getPermission() {
        const deferred = $q.defer();
        if (isSupported()) {
          Notify.requestPermission(deferred.resolve, deferred.reject);
        } else {
          deferred.reject();
        }
        return deferred.promise;
      }

      async function messagesSound() {
        const user = User.getCachedCurrent();
        if (!Boolean(user)) {
          await playSound('likeable');
        } else if (Boolean(user.soundMessage)) {
          await playSound(user.soundMessage);
        }
      }

      async function show(message, data) {
        const user = User.getCachedCurrent();
        if (!Boolean(user?.browserNotifications)) {
          return;
        }

        await getPermission();
        const translations = await getTranslations(message, data);

        const options = {
          body: translations.body,
          icon: 'assets/icons/legacy/logo-notification.png',
        };
        if (!Boolean(Browser.isHandheld())) {
          options.timeout = 2;
          const notification = new Notify(translations.title, options);
          notification.show();
        } else {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(translations.title, options);
        }
      }

      if (Boolean(User.getCachedCurrent()?.browserNotifications)) {
        getPermission();
      }

      async function showCommon(level, key, id, hideDelay, vars) {
        const { level: updatedLevel, hideDelay: updatedHideDelay } =
          updateMessageParams({
            level,
            hideDelay,
          });

        const type = `show${updatedLevel[0].toUpperCase()}${updatedLevel.slice(
          1
        )}`;
        const translationID = `notification.${key}`;
        const hide = Boolean(updatedHideDelay);

        try {
          const translation = await $translate(translationID, vars);

          notifications[type]({
            message: translation,
            hideDelay: updatedHideDelay,
            id: id,
            hide,
          });
        } catch (plainText) {
          if (typeof plainText === 'string') {
            const replacedMessage = plainText.replace('notification.', '');
            notifications[type]({
              message: replacedMessage,
              hideDelay: updatedHideDelay,
              id: id,
              hide,
            });
            $rootScope.$apply();
          }
        }
      }

      async function showError(err) {
        if (Boolean(err) && err.status !== 401) {
          const message =
            err?.message ?? err?.data?.error?.message ?? 'general-server.error';
          await showCommon('error', message);
        }
      }

      function showUnsafeMessage(level, unsafeMessage, id, hideDelay) {
        const { hideDelay: updatedHideDelay, level: updatedLevel } =
          updateMessageParams({ level, hideDelay });
        const hide = Boolean(updatedHideDelay);
        const type = `show${updatedLevel[0].toUpperCase()}${updatedLevel.slice(
          1
        )}`;
        notifications[type]({
          message: unsafeMessage,
          hideDelay: updatedHideDelay,
          id: id,
          hide,
        });
      }

      function isPermissionGranted() {
        // There's no way to know if window.Notification is not supported
        // so just always return true if it's not
        const permissionStatus = getPermissionStatus();
        return (
          angular.isUndefined(permissionStatus) ||
          permissionStatus === 'granted'
        );
      }

      return {
        show,
        showCommon,
        showUnsafeMessage,
        showError,
        getPermission,
        playSound,
        messagesSound,
        isPermissionGranted,
        getPermissionStatus,
        isSupported,
      };
    }
  ).name;
